import { useEffect, useState } from "react";
import { ChevronRight, ScrollText, XCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/shared/TablePagination";
import { ProductTableCell } from "@/components/shared/ProductTableCell";
import { SearchFilterBar } from "@/components/shared/SearchFilterBar";
import { SortableTableHead, getNextSort, type SortDirection } from "@/components/shared/SortableTableHead";
import { formatEasternDateTime } from "@/utils";
import { landscapeLogApi } from "@/resources/landscape-log";
import type { LandscapeLogEntry, LandscapeLogSortField, LandscapeRunStatus } from "@/types";
import {
  RUN_STATUS_LABEL,
  RUN_STATUS_ORDER,
  landscapeRunElapsedLabel,
  landscapeRunKeywordCountDisplay,
  runKindLabel,
} from "@/utils/landscapeRunStatus";
import { HEADER_CLASS, STATUS_FILTER_COLUMNS, STATUS_ICON, STATUS_TEXT_CLASS } from "./landscape-log/constants";
import { StatusIndicator, RetryIndicator } from "./landscape-log/StatusIndicator";
import { LandscapeLogDetailModal } from "./landscape-log/LandscapeLogDetailModal";

export function LandscapeLogTab() {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState<LandscapeRunStatus[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState<LandscapeLogSortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [selectedLog, setSelectedLog] = useState<LandscapeLogEntry | null>(null);
  const [, forceTick] = useState(0);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  // Re-renders once a second so a running (or stopped) row's live Duration
  // keeps counting -- `now`, computed fresh below on every render, is what
  // makes that visible.
  useEffect(() => {
    const id = window.setInterval(() => forceTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const { data, isLoading, isError } = landscapeLogApi.useGetLandscapeLogs({
    status: statusFilter.length ? statusFilter : undefined,
    search: debouncedSearch || undefined,
    page: page + 1,
    per_page: pageSize,
    sort: sortField ?? undefined,
    direction: sortField ? sortDir : undefined,
  });

  // A failed request with no previously loaded page to fall back on --
  // `query.state.data` (see `landscape-log.api.ts`) keeps `data` populated
  // across a failed *background* refetch, so this is deliberately narrower
  // than `isError` alone: it must stay `false` for the "failed refetch
  // after a successful load" case, which keeps showing the stale rows
  // rather than this error state.
  const hasLoadError = isError && !data;

  // Server-ordered (FR-9) -- rendered exactly as `data.data` came back, no
  // client-side re-sort.
  const logs = data?.data ?? [];
  const counts = data?.counts;
  const now = new Date();

  const toggleSort = (field: LandscapeLogSortField) => {
    const next = getNextSort(sortField, sortDir, field);
    setSortField(next.field);
    setSortDir(next.direction);
    setPage(0);
  };

  const toggleKpiFilter = (status: LandscapeRunStatus) => {
    setStatusFilter((prev) => (prev.length === 1 && prev[0] === status ? [] : [status]));
    setPage(0);
  };

  return (
    <div className="space-y-6">
      {/* Counts come from the same failed request as the table below -- in
          the no-data error case there is nothing real to show here, and
          rendering six confident zeroes would be exactly the "0 results"
          lie this fix exists to stop. */}
      {!hasLoadError && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {RUN_STATUS_ORDER.map((status) => {
            const Icon = STATUS_ICON[status];
            const count = counts?.[status] ?? 0;
            const active = statusFilter.includes(status);
            return (
              <Card
                key={status}
                data-testid={`landscape-log-kpi-${status}`}
                className={`cursor-pointer transition-colors hover:border-primary/40 ${active ? "border-primary" : ""}`}
                onClick={() => toggleKpiFilter(status)}
              >
                <CardContent className="py-4 px-4 flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${STATUS_TEXT_CLASS[status]} ${status === "running" ? "animate-spin" : ""}`} />
                  <div>
                    <p className="text-2xl font-semibold text-foreground">{count}</p>
                    <p className="text-xs text-muted-foreground">{RUN_STATUS_LABEL[status]}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <SearchFilterBar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        placeholder="Search landscapes, ASINs, users…"
        searchWidth="flex-1 max-w-xs"
        filterColumns={STATUS_FILTER_COLUMNS}
        activeFilters={{ status: statusFilter }}
        onFiltersChange={(filters) => {
          setStatusFilter((filters.status ?? []) as LandscapeRunStatus[]);
          setPage(0);
        }}
      />

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead field="product" activeField={sortField} direction={sortDir} onSort={toggleSort} className={HEADER_CLASS}>
                  Product
                </SortableTableHead>
                {/* Brand is nullable, so it is not offered for ordering
                    (`landscape/J3`'s guardrail, FR-5) -- a plain, non-sortable
                    header, not a SortableTableHead. */}
                <TableHead className={HEADER_CLASS}>Brand</TableHead>
                <TableHead className={HEADER_CLASS}>Work</TableHead>
                <SortableTableHead field="status" activeField={sortField} direction={sortDir} onSort={toggleSort} className={HEADER_CLASS}>
                  Status
                </SortableTableHead>
                <SortableTableHead field="requested_by" activeField={sortField} direction={sortDir} onSort={toggleSort} className={HEADER_CLASS}>
                  Triggered By
                </SortableTableHead>
                <SortableTableHead field="requested_at" activeField={sortField} direction={sortDir} onSort={toggleSort} className={HEADER_CLASS}>
                  Date Started
                </SortableTableHead>
                <SortableTableHead field="duration" activeField={sortField} direction={sortDir} onSort={toggleSort} className={HEADER_CLASS}>
                  Duration
                </SortableTableHead>
                <SortableTableHead
                  field="keywords"
                  activeField={sortField}
                  direction={sortDir}
                  onSort={toggleSort}
                  tooltip="Keywords found so far for this run"
                  className={HEADER_CLASS}
                >
                  Keywords
                </SortableTableHead>
                <TableHead className={`${HEADER_CLASS} w-8`} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {hasLoadError ? (
                <TableRow>
                  <TableCell colSpan={9} className="p-10">
                    <Alert variant="destructive" className="mx-auto max-w-md">
                      <XCircle className="h-4 w-4" aria-hidden="true" />
                      <AlertTitle>Couldn&apos;t load the landscape log</AlertTitle>
                      <AlertDescription>
                        Something went wrong loading landscape runs. Try again in a moment.
                      </AlertDescription>
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : isLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={`landscape-log-skeleton-${idx}`}>
                    <TableCell colSpan={9}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="p-10 text-center text-muted-foreground text-sm">
                    <div className="flex flex-col items-center gap-2">
                      <ScrollText className="h-6 w-6" />
                      No landscape runs found for this filter.
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow
                    key={log.id}
                    data-testid={`landscape-log-row-${log.id}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedLog(log)}
                  >
                    <TableCell>
                      <ProductTableCell
                        name={log.product_name ?? log.hero_asin ?? "—"}
                        asin={log.hero_asin ?? undefined}
                        imageUrl={log.product_image_url ?? undefined}
                        size="lg"
                        wrapName
                        asinLinkToAmazon
                      />
                    </TableCell>
                    <TableCell
                      className="text-xs text-foreground"
                      data-testid={`landscape-log-brand-${log.id}`}
                    >
                      {log.brand_name ?? "—"}
                    </TableCell>
                    <TableCell data-testid={`landscape-log-kind-${log.id}`}>
                      <p className="text-xs font-medium text-foreground">{runKindLabel(log.kind)}</p>
                      {log.kind === "landscape" && (
                        <p className="text-xs text-muted-foreground">{log.search_phrase ?? "—"}</p>
                      )}
                    </TableCell>
                    <TableCell data-testid={`landscape-log-status-${log.id}`}>
                      <div className="flex items-center">
                        <StatusIndicator status={log.status} />
                        <RetryIndicator count={log.retry_count} />
                      </div>
                    </TableCell>
                    <TableCell
                      className="text-xs text-foreground"
                      data-testid={`landscape-log-requested-by-${log.id}`}
                    >
                      {log.requested_by_label || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {log.requested_at ? formatEasternDateTime(log.requested_at) : "—"}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`landscape-log-duration-${log.id}`}>
                      {landscapeRunElapsedLabel(
                        log.status,
                        log.started_at,
                        log.ended_at,
                        log.state_changed_at,
                        log.requested_at,
                        now,
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground" data-testid={`landscape-log-keywords-${log.id}`}>
                      {landscapeRunKeywordCountDisplay(log.status, log.keyword_count)}
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Same reasoning as the KPI cards: `data?.total` is `0` when
              `data` is undefined, and paginating over an unknown total in
              the no-data error case would repeat the same "0 results" lie
              in a different widget. */}
          {!hasLoadError && (
            <TablePagination
              totalItems={data?.total ?? 0}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
            />
          )}
        </CardContent>
      </Card>

      <LandscapeLogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
}
